/**
 * Resolve department from various API shapes (index vs detail responses,
 * snake_case vs camelCase, object vs JSON string, nested meta/details).
 */
function coerceToRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  if (typeof value === 'string') {
    const t = value.trim()
    if (!t.startsWith('{') && !t.startsWith('[')) return null
    try {
      const p = JSON.parse(t) as unknown
      return typeof p === 'object' && p != null && !Array.isArray(p) ? (p as Record<string, unknown>) : null
    } catch {
      return null
    }
  }
  return null
}

function pickDepartmentFromRecord(rec: Record<string, unknown> | null | undefined): string {
  if (!rec || typeof rec !== 'object') return ''
  const keys = ['department', 'Department', 'dept', 'department_name']
  for (const k of keys) {
    const v = rec[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/** Eloquent-style meta rows: [{ meta_key/meta_name/key, meta_value/value }, ...] */
function pickDepartmentFromMetaRows(arr: unknown): string {
  if (!Array.isArray(arr)) return ''
  const keyAliases = ['meta_key', 'meta_name', 'key', 'name', 'property']
  const valAliases = ['meta_value', 'value', 'val']
  for (const raw of arr) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    let k = ''
    for (const kk of keyAliases) {
      if (row[kk] != null) {
        k = String(row[kk])
        break
      }
    }
    if (!/^department$/i.test(k.trim())) continue
    for (const vk of valAliases) {
      const v = row[vk]
      if (v != null && String(v).trim() !== '') return String(v).trim()
    }
  }
  return ''
}

const NESTED_KEYS = [
  'user_meta',
  'userMeta',
  'user_meta_data',
  'meta',
  'user_details',
  'userDetails',
  'user_detail',
  'userDetail',
  'details',
  'profile',
  'attributes',
] as const

const INNER_META_KEYS = ['user_meta', 'userMeta', 'meta', 'custom_fields'] as const

/** Used by Users table, CSV export, UserForm load, TicketRequestForm, etc. */
export function departmentFromApiUserPayload(user: unknown): string {
  if (!user || typeof user !== 'object') return ''
  const u = user as Record<string, unknown>

  const top = pickDepartmentFromRecord(u)
  if (top) return top

  for (const key of NESTED_KEYS) {
    const rawNested = u[key]
    if (Array.isArray(rawNested)) {
      const fromRows = pickDepartmentFromMetaRows(rawNested)
      if (fromRows) return fromRows
    }
    const rec = coerceToRecord(rawNested)
    let d = pickDepartmentFromRecord(rec)
    if (d) return d
    if (rec) {
      for (const inner of INNER_META_KEYS) {
        d = pickDepartmentFromRecord(coerceToRecord(rec[inner]))
        if (d) return d
      }
    }
  }
  return ''
}
